import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import { useAccountEventsStore } from '@/stores/account-events'

/**
 * The event the API sends on the account channel whenever something on
 * the account's billing changes (a plan change, a renewal, a payment, a card
 * update). It carries nothing; the client reads the billing endpoints again.
 */
export const BILLING_CHANGED_EVENT = 'account-billing-changed'

/**
 * The API sends one event per billing_event, and a single change writes
 * several, so a burst of events becomes one refresh.
 */
export const REFRESH_DEBOUNCE_MS = 300

const HISTORY_PER_PAGE = 20

const emptyPage = () => ({ items: [], pagination: null })

/**
 * Billing state: the summary (with its status alert), the upcoming invoice and
 * the history (invoices and activity).
 *
 * The app banner and the billing screen both read this store, so they never
 * disagree about what needs the owner's attention. The banner needs only the
 * summary; the billing screen asks for the rest with `refreshAll()`. A live
 * event on the account channel refreshes the summary and whatever else has been
 * asked for, so the screen updates without polling.
 */
export const useBillingStore = defineStore('billing', () => {
    const summary = ref(null)
    const upcomingInvoice = ref(null)
    const invoices = ref(emptyPage())
    const activity = ref(emptyPage())

    const loading = ref({
        summary: false,
        upcomingInvoice: false,
        invoices: false,
        activity: false,
    })
    const errors = ref({
        summary: null,
        upcomingInvoice: null,
        invoices: null,
        activity: null,
    })

    /**
     * One thing the store reads. `generation` counts its requests, so only the
     * newest answer is kept. The setters write to the exposed `loading` and
     * `errors`, each by its own key.
     */
    function resource(setLoading, setError) {
        return { generation: 0, setLoading, setError }
    }

    const summaryResource = resource(
        (value) => {
            loading.value.summary = value
        },
        (message) => {
            errors.value.summary = message
        }
    )
    const upcomingInvoiceResource = resource(
        (value) => {
            loading.value.upcomingInvoice = value
        },
        (message) => {
            errors.value.upcomingInvoice = message
        }
    )
    const invoicesResource = resource(
        (value) => {
            loading.value.invoices = value
        },
        (message) => {
            errors.value.invoices = message
        }
    )
    const activityResource = resource(
        (value) => {
            loading.value.activity = value
        },
        (message) => {
            errors.value.activity = message
        }
    )
    const resources = [
        summaryResource,
        upcomingInvoiceResource,
        invoicesResource,
        activityResource,
    ]

    // Not reactive: bookkeeping for the refresh logic, never rendered.
    const requested = new Set()
    const pages = {
        invoices: { page: 1, perPage: HISTORY_PER_PAGE },
        activity: { page: 1, perPage: HISTORY_PER_PAGE },
    }
    const accountEvents = useAccountEventsStore()
    let stopListening = null
    let refreshTimer = null

    /** The one alert the owner should see now, or null. */
    const statusAlert = computed(() => summary.value?.status_alert ?? null)

    /**
     * Run one read, keeping only the answer to the newest request for that
     * resource: a slow older response must not overwrite a newer one.
     */
    async function load(target, request, apply) {
        requested.add(target)
        target.generation += 1
        const generation = target.generation
        const isNewest = () => target.generation === generation
        target.setLoading(true)

        try {
            const response = await request()
            if (!isNewest()) return
            apply(response?.data)
            target.setError(null)
        } catch (error) {
            if (!isNewest()) return
            target.setError(error?.message ?? 'Something went wrong.')
        } finally {
            if (isNewest()) {
                target.setLoading(false)
            }
        }
    }

    function refreshSummary() {
        return load(
            summaryResource,
            () => ProfileAccountBillingService.get(),
            (data) => {
                summary.value = data ?? null
            }
        )
    }

    function refreshUpcomingInvoice() {
        return load(
            upcomingInvoiceResource,
            () => ProfileAccountBillingService.upcomingInvoice(),
            (data) => {
                upcomingInvoice.value = data ?? null
            }
        )
    }

    /**
     * Load a page of invoices; with no argument, reload the page held now.
     *
     * @param {{ page?: number, perPage?: number }} [options]
     * @returns {Promise<void>}
     */
    function refreshInvoices(options = {}) {
        pages.invoices = { ...pages.invoices, ...options }
        return load(
            invoicesResource,
            () => ProfileAccountBillingService.invoices(pages.invoices),
            (data) => {
                invoices.value = {
                    items: data?.items ?? [],
                    pagination: data?.pagination ?? null,
                }
            }
        )
    }

    /**
     * Load a page of activity; with no argument, reload the page held now.
     *
     * @param {{ page?: number, perPage?: number }} [options]
     * @returns {Promise<void>}
     */
    function refreshActivity(options = {}) {
        pages.activity = { ...pages.activity, ...options }
        return load(
            activityResource,
            () => ProfileAccountBillingService.activity(pages.activity),
            (data) => {
                activity.value = {
                    items: data?.items ?? [],
                    pagination: data?.pagination ?? null,
                }
            }
        )
    }

    /** The history is the invoices and the activity. */
    function refreshHistory() {
        return Promise.all([refreshInvoices(), refreshActivity()])
    }

    /** Everything the billing screen shows. */
    function refreshAll() {
        return Promise.all([
            refreshSummary(),
            refreshUpcomingInvoice(),
            refreshHistory(),
        ])
    }

    /**
     * What a billing change event refreshes: the summary always, and the rest
     * only if something has asked for it (the banner alone never loads the
     * invoice list).
     */
    function refreshRequested({ summary: includeSummary = true } = {}) {
        const jobs = includeSummary ? [refreshSummary()] : []
        if (requested.has(upcomingInvoiceResource)) {
            jobs.push(refreshUpcomingInvoice())
        }
        if (requested.has(invoicesResource)) jobs.push(refreshInvoices())
        if (requested.has(activityResource)) jobs.push(refreshActivity())
        return Promise.all(jobs)
    }

    /**
     * Take a summary the API sent back with a change (a card saved, a
     * scheduled change undone, a plan change confirmed). It is newer than any
     * read still in flight, so those are dropped, and what hangs off the
     * summary (the upcoming invoice and the history) is read again.
     *
     * @param {object|null} data - The billing summary
     * @returns {Promise<void>}
     */
    function applySummary(data) {
        summaryResource.generation += 1
        summary.value = data ?? null
        errors.value.summary = null
        loading.value.summary = false

        return refreshRequested({ summary: false }).then(() => {})
    }

    function scheduleRefresh() {
        clearTimeout(refreshTimer)
        refreshTimer = setTimeout(() => {
            refreshTimer = null
            void refreshRequested()
        }, REFRESH_DEBOUNCE_MS)
    }

    /** Stop following changes. The shared account connection stays open. */
    function unlisten() {
        clearTimeout(refreshTimer)
        refreshTimer = null

        stopListening?.()
        stopListening = null
    }

    /** Refresh on billing changes, and again once the connection is back. */
    function listen() {
        if (stopListening) return

        const offBillingChanged = accountEvents.on(
            BILLING_CHANGED_EVENT,
            scheduleRefresh
        )
        const offResync = accountEvents.onResync(scheduleRefresh)

        stopListening = () => {
            offBillingChanged()
            offResync()
        }
    }

    /** Back to the empty state, and any request still in flight is ignored. */
    function reset() {
        for (const target of resources) {
            target.generation += 1
            target.setLoading(false)
            target.setError(null)
        }
        requested.clear()
        pages.invoices = { page: 1, perPage: HISTORY_PER_PAGE }
        pages.activity = { page: 1, perPage: HISTORY_PER_PAGE }
        summary.value = null
        upcomingInvoice.value = null
        invoices.value = emptyPage()
        activity.value = emptyPage()
    }

    /**
     * Start following the account's billing: load the summary and listen for
     * changes. Safe to call again. The account connection is opened by the
     * account events store, so this only registers for billing events.
     *
     * @returns {Promise<void>}
     */
    function start() {
        listen()
        return refreshSummary()
    }

    /** Stop listening and forget everything, e.g. on sign-out. */
    function stop() {
        unlisten()
        reset()
    }

    return {
        summary,
        upcomingInvoice,
        invoices,
        activity,
        loading,
        errors,
        statusAlert,
        refreshSummary,
        refreshUpcomingInvoice,
        refreshInvoices,
        refreshActivity,
        refreshHistory,
        refreshAll,
        applySummary,
        start,
        stop,
    }
})
